import type { FC, FormEvent, ReactNode } from 'react'

import type {
    Catalog,
    CatalogProduct,
    CatalogStatus,
    FieldErrors,
    Movement,
    MovementType
} from '@/types'

import { useId, useMemo, useState } from 'react'

import {
    ArrowRight,
    CircleX,
    LoaderCircle,
    Minus,
    Plus,
    TriangleAlert
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { ApiError, apiRequest, getErrorMessage } from '@/lib/api'
import { getStockStatus } from '@/lib/format'
import { parseWholeNumber, validateMovement } from '@/lib/validation'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { useSettings } from '@/components/generals/settings-provider'
import { MOVEMENT_ICONS } from '@/components/inventory/movement-type-badge'
import FormField, { describedBy } from '@/components/generals/form-field'
import SegmentedControl from '@/components/generals/segmented-control'
import ProductPicker from '@/components/inventory/product-picker'
import UnitLabel from '@/components/inventory/unit-label'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

import {
    LIMITS,
    MOVEMENT_REASONS,
    MOVEMENT_TYPE_META,
    MOVEMENT_TYPES
} from '@/constants/inventory'

interface MovementDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    requestKey: number
    initialType: MovementType
    initialProduct: CatalogProduct | null
    catalog: Catalog | null
    catalogStatus: CatalogStatus
    onReloadCatalog: () => void
    onSaved: (movement: Movement) => void
}

interface MovementFormProps extends Omit<MovementDialogProps, 'open' | 'requestKey'> {
    searchId: string
    quantityId: string
}

const TYPE_OPTIONS = MOVEMENT_TYPES.map(type => ({
    value: type,
    label: MOVEMENT_TYPE_META[type].label,
    icon: MOVEMENT_ICONS[type]
}))

const MovementForm: FC<MovementFormProps> = ({
    onOpenChange,
    initialType,
    initialProduct,
    catalog,
    catalogStatus,
    onReloadCatalog,
    onSaved,
    searchId,
    quantityId
}): ReactNode => {
    const { number } = useSettings()
    const baseId = useId()
    const reasonId = `${baseId}-reason`
    const noteId = `${baseId}-note`

    const [type, setType] = useState<MovementType>(initialType)
    const [productId, setProductId] = useState<string | null>(initialProduct?.id ?? null)
    const [quantity, setQuantity] = useState(initialType === 'ajuste' && initialProduct ? String(initialProduct.quantity) : '')
    const [reason, setReason] = useState(MOVEMENT_REASONS[initialType][0])
    const [note, setNote] = useState('')
    const [errors, setErrors] = useState<FieldErrors>({})
    const [formError, setFormError] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)

    const product = useMemo<CatalogProduct | null>(() => {
        if (!productId) return null

        const fresh = catalog?.products.find(item => item.id === productId)

        if (fresh) return fresh
        if (initialProduct?.id === productId) return initialProduct

        return null
    }, [catalog, productId, initialProduct])

    const meta = MOVEMENT_TYPE_META[type]
    const parsed = parseWholeNumber(quantity)
    const amount = parsed !== null && !Number.isNaN(parsed) && parsed >= 0 ? parsed : null
    const current = product?.quantity ?? 0

    let next: number | null = null

    if (product && amount !== null) {
        if (type === 'entrada') next = current + amount
        if (type === 'salida') next = current - amount
        if (type === 'ajuste') next = amount
    }

    const exceeds = type === 'salida' && product !== null && amount !== null && amount > current
    const nextStatus = next !== null && next >= 0 && product ? getStockStatus(next, product.minStock) : null

    const handleTypeChange = (value: MovementType) => {
        setType(value)
        setReason(MOVEMENT_REASONS[value][0])
        setErrors({})
        setFormError(null)

        if (value === 'ajuste') setQuantity(product ? String(product.quantity) : '')
        else if (type === 'ajuste') setQuantity('')
    }

    const handleSelectProduct = (selected: CatalogProduct) => {
        setProductId(selected.id)
        setErrors(previous => ({ ...previous, productId: undefined }))

        if (type === 'ajuste') setQuantity(String(selected.quantity))

        requestAnimationFrame(() => document.getElementById(quantityId)?.focus())
    }

    const handleStep = (delta: number) => {
        const minimum = type === 'ajuste' ? 0 : 1
        const base = amount ?? 0

        setQuantity(String(Math.max(minimum, base + delta)))
        setErrors(previous => ({ ...previous, quantity: undefined }))
    }

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setFormError(null)

        const result = validateMovement({ productId, type, quantity, reason, note })

        if (!result.ok) {
            setErrors(result.errors)

            return
        }

        if (exceeds && product) {
            setErrors({ quantity: `Solo quedan ${number(current)} ${product.unit}. No puedes sacar más de lo que hay.` })

            return
        }

        setErrors({})
        setSubmitting(true)

        try {
            const { movement } = await apiRequest<{ movement: Movement }>('/api/movements', {
                method: 'POST',
                body: result.value
            })

            onSaved(movement)
        }
        catch (error) {
            if (error instanceof ApiError) {
                setErrors(error.fieldErrors)

                if (!Object.keys(error.fieldErrors).length) setFormError(error.message)
                if (error.status === 409 || error.status === 404) onReloadCatalog()
            }
            else {
                setFormError(getErrorMessage(error))
            }
        }
        finally {
            setSubmitting(false)
        }
    }

    return (
        <form onSubmit={handleSubmit} noValidate className='flex min-h-0 flex-1 flex-col'>
            <DialogHeader className='border-b px-5 py-4 pr-12'>
                <DialogTitle>
                    Registrar movimiento
                </DialogTitle>

                <DialogDescription>
                    Anota lo que entra, lo que sale o el recuento real de un producto.
                </DialogDescription>
            </DialogHeader>

            <div className='flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-5'>
                {formError && (
                    <div role='alert' className='flex items-start gap-2 rounded-lg bg-danger-soft px-3 py-2.5 text-sm text-danger-ink'>
                        <CircleX className='mt-0.5 size-4 shrink-0' aria-hidden='true' />
                        {formError}
                    </div>
                )}

                <SegmentedControl
                    label='Tipo de movimiento'
                    value={type}
                    options={TYPE_OPTIONS}
                    onChange={handleTypeChange}
                    stretch
                />

                <FormField
                    id={searchId}
                    label='Producto'
                    error={errors.productId}
                >
                    {product ? (
                        <div className='flex items-center justify-between gap-3 rounded-lg border bg-muted/40 px-3 py-2.5'>
                            <div className='min-w-0'>
                                <p className='truncate text-sm font-medium'>
                                    <span data-mdlfy-cms={`products:${product.id}:name`}>{product.name}</span>
                                </p>

                                <p className='mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-muted-foreground'>
                                    {product.sku && (
                                        <span className='font-mono' data-mdlfy-cms={`products:${product.id}:sku`}>{product.sku}</span>
                                    )}

                                    <span className='tabular-nums'>
                                        Stock: <span>{number(product.quantity)}</span> <UnitLabel productId={product.id} unit={product.unit} />
                                    </span>
                                </p>
                            </div>

                            <Button
                                type='button'
                                variant='outline'
                                size='sm'
                                onClick={() => {
                                    setProductId(null)
                                    requestAnimationFrame(() => document.getElementById(searchId)?.focus())
                                }}
                            >
                                Cambiar
                            </Button>
                        </div>
                    ) : (
                        <ProductPicker
                            inputId={searchId}
                            products={catalog?.products ?? []}
                            status={catalogStatus}
                            onRetry={onReloadCatalog}
                            onSelect={handleSelectProduct}
                            invalid={Boolean(errors.productId)}
                            describedById={describedBy(searchId, errors.productId)}
                        />
                    )}
                </FormField>

                <FormField
                    id={quantityId}
                    label={meta.quantityLabel}
                    hint={meta.quantityHint}
                    error={errors.quantity}
                >
                    <div className='flex items-center gap-2'>
                        <Button
                            type='button'
                            variant='outline'
                            size='icon-lg'
                            aria-label='Restar una unidad'
                            onClick={() => handleStep(-1)}
                        >
                            <Minus aria-hidden='true' />
                        </Button>

                        <Input
                            id={quantityId}
                            inputMode='numeric'
                            pattern='[0-9]*'
                            autoComplete='off'
                            placeholder='0'
                            value={quantity}
                            onChange={event => {
                                setQuantity(event.target.value)
                                setErrors(previous => ({ ...previous, quantity: undefined }))
                            }}
                            aria-invalid={Boolean(errors.quantity) || exceeds || undefined}
                            aria-describedby={describedBy(quantityId, errors.quantity, meta.quantityHint)}
                            className='h-10 text-center text-lg font-semibold tabular-nums md:text-base'
                        />

                        <Button
                            type='button'
                            variant='outline'
                            size='icon-lg'
                            aria-label='Sumar una unidad'
                            onClick={() => handleStep(1)}
                        >
                            <Plus aria-hidden='true' />
                        </Button>
                    </div>
                </FormField>

                {product && (
                    <div className='rounded-lg border bg-muted/40 px-4 py-3' aria-live='polite'>
                        <div className='flex items-center justify-between gap-3'>
                            <div>
                                <p className='text-xs text-muted-foreground'>
                                    Stock actual
                                </p>

                                <p className='font-heading text-lg font-semibold tabular-nums'>
                                    <span>{number(current)}</span> <UnitLabel productId={product.id} unit={product.unit} className='text-sm font-medium text-muted-foreground' />
                                </p>
                            </div>

                            <ArrowRight className='size-4 text-muted-foreground' aria-hidden='true' />

                            <div className='text-right'>
                                <p className='text-xs text-muted-foreground'>
                                    Nuevo stock
                                </p>

                                <p
                                    className={cn(
                                        'font-heading text-lg font-semibold tabular-nums',
                                        exceeds && 'text-danger-ink',
                                        !exceeds && nextStatus === 'out' && 'text-danger-ink',
                                        !exceeds && nextStatus === 'low' && 'text-warning-ink'
                                    )}
                                >
                                    <span>{next === null ? '-' : number(next)}</span> <UnitLabel productId={product.id} unit={product.unit} className='text-sm font-medium text-muted-foreground' />
                                </p>
                            </div>
                        </div>

                        {exceeds && (
                            <p className='mt-2 flex items-start gap-1.5 text-xs font-medium text-danger-ink'>
                                <CircleX className='mt-px size-3.5 shrink-0' aria-hidden='true' />
                                No hay suficiente stock para esta salida.
                            </p>
                        )}

                        {!exceeds && nextStatus === 'out' && type !== 'entrada' && (
                            <p className='mt-2 flex items-start gap-1.5 text-xs font-medium text-danger-ink'>
                                <TriangleAlert className='mt-px size-3.5 shrink-0' aria-hidden='true' />
                                El producto quedará agotado.
                            </p>
                        )}

                        {!exceeds && nextStatus === 'low' && (
                            <p className='mt-2 flex items-start gap-1.5 text-xs font-medium text-warning-ink'>
                                <TriangleAlert className='mt-px size-3.5 shrink-0' aria-hidden='true' />
                                <span>
                                    Quedará por debajo del stock mínimo (<span>{number(product.minStock)}</span> <UnitLabel productId={product.id} unit={product.unit} />).
                                </span>
                            </p>
                        )}
                    </div>
                )}

                <div className='grid gap-4 sm:grid-cols-2'>
                    <FormField id={reasonId} label='Motivo' error={errors.reason}>
                        <NativeSelect
                            id={reasonId}
                            value={reason}
                            onChange={event => setReason(event.target.value)}
                            className='w-full'
                        >
                            {MOVEMENT_REASONS[type].map(option => (
                                <NativeSelectOption key={option} value={option}>
                                    {option}
                                </NativeSelectOption>
                            ))}
                        </NativeSelect>
                    </FormField>

                    <FormField id={noteId} label='Nota' optional error={errors.note}>
                        <Input
                            id={noteId}
                            value={note}
                            maxLength={LIMITS.note}
                            placeholder='Ej.: pedido 1048, albarán 2320'
                            onChange={event => setNote(event.target.value)}
                            aria-invalid={Boolean(errors.note) || undefined}
                            aria-describedby={describedBy(noteId, errors.note)}
                        />
                    </FormField>
                </div>
            </div>

            <DialogFooter className='mx-0 mb-0 rounded-b-xl px-5 py-4'>
                <DialogClose render={<Button type='button' variant='outline' />}>
                    Cancelar
                </DialogClose>

                <Button type='submit' disabled={submitting || exceeds}>
                    {submitting && <LoaderCircle className='animate-spin' aria-hidden='true' />}
                    {submitting ? 'Guardando…' : meta.submitLabel}
                </Button>
            </DialogFooter>
        </form>
    )
}

const MovementDialog: FC<MovementDialogProps> = ({ open, requestKey, ...props }): ReactNode => {
    const baseId = useId()
    const searchId = `${baseId}-search`
    const quantityId = `${baseId}-quantity`

    return (
        <Dialog open={open} onOpenChange={nextOpen => props.onOpenChange(nextOpen)}>
            <DialogContent
                className='flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg'
                initialFocus={() => document.getElementById(props.initialProduct ? quantityId : searchId) ?? true}
            >
                <MovementForm
                    key={requestKey}
                    {...props}
                    searchId={searchId}
                    quantityId={quantityId}
                />
            </DialogContent>
        </Dialog>
    )
}

export default MovementDialog
